import { groupChatMessage, sharedGroupValue } from "../../utils/socialDepth";
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { V, Haptic } from '../../utils/theme';
import { LS } from '../../utils/storage';
import { Icons } from '../../components/Icons';
import { SectionTitle } from '../../components/kit';
import { Card, Btn, Field, Sheet, Chip, Stat, Progress, Skeleton, SkeletonCard, MsgBannerCtrl, SuccessToastCtrl, ConfirmCtrl } from '../../components/ui';
import { today, ago, fmtShort, fmtFull, uid, friendDisplayName, convW, wUnit, calc1RM, calcStreak } from '../../utils/helpers';
import { SocialAPI, SYNC_URL } from '../../utils/sync';
import { ShareCard } from '../../utils/share';
import { BADGE_DEFS, calcEarnedBadges } from '../../data/badges';
import { useLayout } from '../../utils/responsive';
import { IRON_RANKS } from '../../data/ranks';
import { HelpBtn, compressImage } from '../features';
import { IronScoreCard, RankBadge, DailyMissionsCard } from '../gamification';
import { useStreak, calcReadiness } from '../../components/dialogs';
import { SectionGrid } from '../hubs';
import { MS_PER_MINUTE, MS_PER_HOUR, MS_PER_DAY, ONLINE_THRESHOLD_MS } from '../../utils/constants';
import { apiPost } from '../../utils/api';
import { SocialErrorCard, ReactionRow, applyReactionToggle } from './shared';

export function SocialGroups({s,d}){
  const { isDesktop } = useLayout();
  const email=s.profile?.email;
  const displayName=s.profile?.firstName||email?.split("@")[0]||"You";
  const [groups,setGroups]=useState(null);
  const [code,setCode]=useState("");
  const [newName,setNewName]=useState("");
  const [showCreate,setShowCreate]=useState(false);
  const [loadErr,setLoadErr]=useState(false);
  const [selGroup,setSelGroup]=useState(null);
  const [members,setMembers]=useState(null);
  // Tracks the currently-viewed group so a late getMembers() response from a previous
  // group (or one fired after the user navigated away) can't setMembers on a stale view
  const selGroupRef=useRef(null);
  useEffect(()=>{selGroupRef.current=selGroup;},[selGroup]);
  const refreshMembers=(gc)=>{
    SocialAPI.getMembers(email,gc).then(m=>{if(selGroupRef.current===gc)setMembers(m);});
  };
  const [groupTab,setGroupTab]=useState("board");
  const [lbMetric,setLbMetric]=useState("streak");
  const [chatMsg,setChatMsg]=useState("");
  const [chatMessages,setChatMessages]=useState([]);
  const [newChal,setNewChal]=useState({name:"",metric:"workouts",target:"",endDate:"",mode:"shared"});
  const [showNewChal,setShowNewChal]=useState(false);
  const [groupChals,setGroupChals]=useState([]);
  const [groupFeed,setGroupFeed]=useState([]);
  const streak=useStreak(s.workouts);
  // Global discover groups
  const [showDiscoverG,setShowDiscoverG]=useState(false);
  const [discoverGQ,setDiscoverGQ]=useState("");
  const [discoverGResults,setDiscoverGResults]=useState(null);
  const [discoverGLoading,setDiscoverGLoading]=useState(false);
  const [discoverGPage,setDiscoverGPage]=useState(1);
  // Monotonic counter so an older in-flight discover response can't overwrite a newer one
  const discoverGSeqRef=useRef(0);
  const discoverGSearch=(q,page)=>{
    const seq=++discoverGSeqRef.current;
    setDiscoverGLoading(true);
    SocialAPI.discoverGroups(email,q||"",page||1).then(r=>{
      if(seq!==discoverGSeqRef.current)return;
      setDiscoverGResults(r);setDiscoverGLoading(false);
    });
  };
  useEffect(()=>{if(showDiscoverG)discoverGSearch(discoverGQ,1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[showDiscoverG,discoverGQ]);

  const loadGroups=()=>{
    setLoadErr(false);
    if(!email)return;
    SocialAPI.getGroups(email)
      .then(r=>{if(r)setGroups(r);else setLoadErr(true);})
      .catch(()=>setLoadErr(true));
  };
  useEffect(loadGroups,[email]);

  const join=async()=>{if(code.length<4)return;const r=await SocialAPI.joinGroup(email,code);
    if(r?.success){SuccessToastCtrl.show(`Joined ${r.group?.name||code}`);setCode("");loadGroups();}else SuccessToastCtrl.error(r?.error||"Failed");};
  const create=async()=>{if(!newName.trim())return;const r=await SocialAPI.createGroup(email,newName);
    if(r?.success){SuccessToastCtrl.show(`Created! Code: ${r.code}`);setNewName("");setShowCreate(false);loadGroups();}};
  const leave=async(c2)=>{ConfirmCtrl.show("Leave this group?","You can rejoin with the group code.",async()=>{await SocialAPI.leaveGroup(email,c2);loadGroups();SuccessToastCtrl.show("Left group");});};

  const openGroup=(gc)=>{
    const sessionToken=LS.get("ft-session-token");
    const sessionEmail=LS.get("ft-session-email");
    const isCurrent=()=>selGroupRef.current===gc&&LS.get("ft-session-token")===sessionToken&&LS.get("ft-session-email")===sessionEmail;
    selGroupRef.current=gc;setSelGroup(gc);setGroupTab("board");
    refreshMembers(gc);
    // Load from localStorage cache first, then refresh from API
    setGroupChals(LS.get(`ft-gchal-${gc}`)||[]);
    setChatMessages(LS.get(`ft-gchat-${gc}`)||[]);
    // Fetch group chat from API
    SocialAPI.getGroupEvents(email,gc,"GroupChat").then(r=>{
      if(isCurrent()&&Array.isArray(r?.events)){
        const msgs=[...r.events].reverse().map(groupChatMessage);
        setChatMessages(msgs);LS.set(`ft-gchat-${gc}`,msgs);
      }
    });
    // Fetch group activity feed
    SocialAPI.getGroupEvents(email,gc).then(r=>{
      if(isCurrent()&&r?.events){
        const feed=r.events.filter(e=>e.type!=="GroupChat"&&e.type!=="GroupChallengeCreated").map(e=>({
          id:e.id,name:e.name,email:e.email,type:e.type,data:e.data,created_at:e.created_at,isOwn:e.email===email,
          reactions:e.reactions||{},my_reaction:e.my_reaction||null}));
        setGroupFeed(feed);
      }
    });
    // Fetch group challenges from API
    SocialAPI.getGroupEvents(email,gc,"GroupChallengeCreated").then(r=>{
      if(isCurrent()&&r?.events?.length>0){
        const chals=r.events.map(e=>({id:e.id,name:e.data?.challenge||"Challenge",metric:e.data?.metric||"streak",
          target:e.data?.target||null,created:e.created_at?.split("T")[0],createdBy:e.email,mode:e.data?.mode||"individual",endDate:e.data?.endDate||null}));
        const merged=[...chals,...(LS.get(`ft-gchal-${gc}`)||[]).filter(lc=>!chals.some(ac=>ac.name===lc.name))];
        setGroupChals(merged);LS.set(`ft-gchal-${gc}`,merged);
      }
    });
  };

  const sendChat=()=>{
    if(!chatMsg.trim()||chatMsg.length>200)return;
    Haptic.light();
    const msg={id:Date.now(),from:email,name:displayName,text:chatMsg.trim(),ts:new Date().toISOString()};
    const updated=[...chatMessages,msg].slice(-50);
    setChatMessages(updated);LS.set(`ft-gchat-${selGroup}`,updated);
    // "group", not "friends". The feed serves every event marked friends to ALL of the
    // sender's friends, and group_events reads by group membership without looking at
    // visibility at all — so marking group chat "friends" published private group
    // messages, text included, to friends who are not in the group, while changing
    // nothing about delivery to the group. iOS has always posted "group".
    SocialAPI.logEvent(email,"GroupChat",{group:selGroup,text:chatMsg.trim(),name:displayName},"group");
    setChatMsg("");
  };

  const createChal=()=>{
    if(!newChal.name||!newChal.target)return;
    const ch={id:uid(),name:newChal.name,metric:newChal.metric,target:parseInt(newChal.target),
      endDate:newChal.endDate||null,created:today(),createdBy:email,mode:newChal.mode||"individual"};
    const updated=[...groupChals,ch];
    setGroupChals(updated);LS.set(`ft-gchal-${selGroup}`,updated);
    SocialAPI.logEvent(email,"GroupChallengeCreated",{group:selGroup,challenge:ch.name,metric:ch.metric,target:ch.target,mode:ch.mode,endDate:ch.endDate},"group");
    SuccessToastCtrl.show(ch.mode==="shared"?"Shared goal created — everyone's progress counts":"Challenge created!");
    setShowNewChal(false);setNewChal({name:"",metric:"workouts",target:"",endDate:"",mode:"shared"});
  };

  const removeChal=(id)=>{const updated=groupChals.filter(c2=>c2.id!==id);setGroupChals(updated);LS.set(`ft-gchal-${selGroup}`,updated);};

  // ─── Group Detail View ───
  if(selGroup&&members)return(
    <div style={{display:"flex",flexDirection:"column",gap:10}}>
      <button onClick={()=>{setSelGroup(null);setMembers(null);}} style={{background:"none",border:"none",display:"flex",alignItems:"center",gap:5,padding:"8px 0",cursor:"pointer"}}>
        {Icons.chevLeft({size:18,color:V.accent})}<span style={{fontSize:14,color:V.accent,fontWeight:600}}>Groups</span>
      </button>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
        <div>
          <div style={{fontSize:16,fontWeight:800,color:V.text}}>{groups?.groups?.find(g=>g.code===selGroup)?.name||selGroup}</div>
          <div style={{fontSize:11,color:V.text3}}>{selGroup} · {members?.members?.length||0} members</div>
        </div>
        <button onClick={()=>{navigator.clipboard?.writeText(selGroup);SuccessToastCtrl.show("Code copied");}} style={{padding:"4px 10px",borderRadius:6,background:`${V.accent}08`,border:`1px solid ${V.accent}30`,cursor:"pointer",fontSize:11,color:V.accent,fontWeight:600,fontFamily:V.font}}>Copy Code</button>
      </div>

      {/* Pinned announcement */}
      {(()=>{const g=groups?.groups?.find(gg=>gg.code===selGroup);const pin=g?.pinned_message;if(!pin)return null;
        const myRole=members?.members?.find(m=>m.email===email)?.role;const canEdit=myRole==="owner"||myRole==="admin";
        return(<Card style={{padding:10,background:`${V.accent}06`,border:`1px solid ${V.accent}20`}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:8}}>
            <div style={{flex:1}}><div style={{fontSize:11,fontWeight:700,color:V.accent,marginBottom:3}}>ANNOUNCEMENT</div>
              <div style={{fontSize:12,color:V.text,lineHeight:1.4}}>{pin.text}</div></div>
            {canEdit&&<button onClick={()=>ConfirmCtrl.show("Remove announcement?","",async()=>{await SocialAPI.pinMessage(email,selGroup,null);loadGroups();})}
              style={{background:"none",border:"none",cursor:"pointer",fontSize:11,color:V.text3,padding:4}}>✕</button>}
          </div>
        </Card>);})()}

      {/* Tab bar */}
      <div style={{display:"flex",gap:0,background:V.card,borderRadius:10,padding:3,border:`1px solid ${V.cardBorder}`}}>
        {[{id:"board",l:"Board"},{id:"challenges",l:"Challenges"},{id:"feed",l:"Feed"},{id:"chat",l:"Chat"},{id:"members",l:"Members"}].map(t=>(
          <button key={t.id} onClick={()=>setGroupTab(t.id)} style={{flex:1,padding:"8px 4px",borderRadius:8,
            background:groupTab===t.id?V.accent:"transparent",border:"none",cursor:"pointer",
            fontSize:11,fontWeight:groupTab===t.id?700:500,color:groupTab===t.id?V.bg:V.text3,fontFamily:V.font}}>{t.l}</button>
        ))}
      </div>

      {/* LEADERBOARD TAB */}
      {groupTab==="board"&&(()=>{
        const LB_METRICS=[{id:"streak",label:"Streak",icon:"🔥",cid:"streak",fmt:(v)=>`${v}d`},{id:"ironscore",label:"XP",icon:"⚡",cid:"ironscore",fmt:(v)=>v>=1000?`${(v/1000).toFixed(1)}k`:String(v)},{id:"big3",label:"Big 3",icon:"🏋️",cid:"big3",fmt:(v)=>`${v}lb`}];
        const getV=(m2,cid)=>parseInt(m2.challenges?.find(c3=>c3.challenge_id===cid)?.value||0);
        const curM=LB_METRICS.find(m2=>m2.id===lbMetric)||LB_METRICS[0];
        const sortedM=[...(members?.members||[])].sort((a,b)=>getV(b,curM.cid)-getV(a,curM.cid));
        const topV=Math.max(1,...sortedM.map(m2=>getV(m2,curM.cid)));
        return(<>
        <Card style={{padding:14}}>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8,marginBottom:10}}>
            {[{l:"MEMBERS",v:members?.members?.length||0,c:V.accent},
              {l:"AVG STREAK",v:Math.round((members?.members||[]).reduce((a,m2)=>a+getV(m2,"streak"),0)/Math.max(1,(members?.members||[]).length))+"d",c:V.warn},
              {l:"TOP STREAK",v:Math.max(0,...(members?.members||[]).map(m2=>getV(m2,"streak")))+"d",c:V.purple}
            ].map((st,i)=>(
              <div key={i} style={{textAlign:"center",padding:6,borderRadius:6,background:V.card}}>
                <div style={{fontSize:16,fontWeight:800,color:st.c,fontFamily:V.mono}}>{st.v}</div>
                <div style={{fontSize:11,color:V.text3}}>{st.l}</div>
              </div>
            ))}
          </div>
          <div style={{display:"flex",gap:4,marginBottom:12}}>
            {LB_METRICS.map(m2=>(
              <button key={m2.id} onClick={()=>setLbMetric(m2.id)} style={{flex:1,padding:"6px 4px",borderRadius:7,
                border:`1.5px solid ${lbMetric===m2.id?V.accent:V.cardBorder}`,
                background:lbMetric===m2.id?`${V.accent}10`:"transparent",cursor:"pointer",
                fontSize:11,fontWeight:700,color:lbMetric===m2.id?V.accent:V.text3,fontFamily:V.font}}>
                {m2.icon} {m2.label}
              </button>
            ))}
          </div>
          {sortedM.map((m,i)=>{
            const val=getV(m,curM.cid);
            const barPct=Math.round(val/topV*100);
            const medal=i===0?"🥇":i===1?"🥈":i===2?"🥉":String(i+1);
            return(
              <div key={i} style={{marginBottom:i<sortedM.length-1?8:0}}>
                <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:3}}>
                  <div style={{width:22,height:22,borderRadius:6,flexShrink:0,display:"flex",alignItems:"center",justifyContent:"center",
                    fontSize:i<3?14:9,fontWeight:800,color:V.text3}}>{medal}</div>
                  <div style={{flex:1,fontSize:12,fontWeight:600,color:m.user_email===email?V.accent:V.text,
                    overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                    {m.name}{m.user_email===email?" (you)":""}
                  </div>
                  <div style={{fontSize:12,fontWeight:800,color:i===0?"#f59e0b":V.text,fontFamily:V.mono,flexShrink:0}}>
                    {curM.fmt(val)}
                  </div>
                </div>
                <div style={{height:3,borderRadius:2,background:V.hoverBg,marginLeft:30}}>
                  <div style={{height:"100%",borderRadius:2,background:i===0?"#f59e0b":V.accent,width:`${barPct}%`,transition:"width .4s"}}/>
                </div>
              </div>
            );
          })}
        </Card>
        </>);
      })()}

      {/* CHALLENGES TAB */}
      {groupTab==="challenges"&&(<>
        <Btn full onClick={()=>setShowNewChal(!showNewChal)}>{showNewChal?"Cancel":"+ New Challenge"}</Btn>
        {showNewChal&&(
          <Card style={{padding:14}}>
            <input value={newChal.name} onChange={e=>setNewChal(c2=>({...c2,name:e.target.value}))} placeholder="Challenge name"
              style={{width:"100%",padding:"8px 12px",background:V.inputBg,border:`1px solid ${V.cardBorder}`,borderRadius:8,color:V.text,fontSize:12,outline:"none",fontFamily:V.font,boxSizing:"border-box",marginBottom:8}}/>
            <div style={{display:"flex",gap:4,marginBottom:8}}>
              {["streak","volume","workouts","protein"].map(m2=>(
                <button key={m2} onClick={()=>setNewChal(c2=>({...c2,metric:m2}))} style={{flex:1,padding:"6px",borderRadius:6,
                  border:`1.5px solid ${newChal.metric===m2?V.accent:V.cardBorder}`,background:newChal.metric===m2?`${V.accent}08`:"transparent",
                  cursor:"pointer",fontSize:11,fontWeight:600,color:newChal.metric===m2?V.accent:V.text3,fontFamily:V.font,textTransform:"capitalize"}}>{m2}</button>
              ))}
            </div>
            <div style={{display:"flex",gap:4,marginBottom:8}}>
              {[{id:"shared",l:"Shared goal",d:"Everyone's progress adds up"},{id:"individual",l:"Each member",d:"Everyone hits the target alone"}].map(m2=>(
                <button key={m2.id} type="button" onClick={()=>setNewChal(c2=>({...c2,mode:m2.id}))} style={{flex:1,padding:"8px",borderRadius:8,textAlign:"left",
                  border:`1.5px solid ${newChal.mode===m2.id?V.accent:V.cardBorder}`,background:newChal.mode===m2.id?`${V.accent}08`:"transparent",cursor:"pointer",fontFamily:V.font}}>
                  <div style={{fontSize:12,fontWeight:700,color:newChal.mode===m2.id?V.accent:V.text}}>{m2.l}</div>
                  <div style={{fontSize:11,color:V.text3}}>{m2.d}</div>
                </button>
              ))}
            </div>
            <div style={{display:"flex",gap:8,marginBottom:8}}>
              <input value={newChal.target} onChange={e=>setNewChal(c2=>({...c2,target:e.target.value}))} placeholder="Target" type="number"
                style={{flex:1,padding:"8px 12px",background:V.inputBg,border:`1px solid ${V.cardBorder}`,borderRadius:8,color:V.text,fontSize:12,outline:"none",fontFamily:V.mono}}/>
              <input value={newChal.endDate} onChange={e=>setNewChal(c2=>({...c2,endDate:e.target.value}))} type="date"
                style={{flex:1,padding:"8px 12px",background:V.inputBg,border:`1px solid ${V.cardBorder}`,borderRadius:8,color:V.text,fontSize:12,outline:"none",fontFamily:V.mono}}/>
            </div>
            <Btn full onClick={createChal} disabled={!newChal.name||!newChal.target}>Create Challenge</Btn>
          </Card>
        )}
        {groupChals.length>0?groupChals.map((ch,i)=>{
          const individualVal=ch.metric==="streak"?streak:
            ch.metric==="workouts"?(s.workouts||[]).filter(w=>w.date>=ch.created).length:
            ch.metric==="volume"?(s.workouts||[]).filter(w=>w.date>=ch.created).reduce((a2,w)=>a2+w.exercises.reduce((b2,e)=>b2+e.sets.reduce((c2,st)=>c2+(parseFloat(st.weight)||0)*(parseInt(st.reps)||0),0),0),0):
            ch.metric==="protein"?(s.nutrition||[]).filter(n=>n.date>=ch.created).reduce((a2,n)=>a2+(n.protein||0),0):0;
          const myVal=ch.mode==="shared"?sharedGroupValue(s,ch.metric,{since:ago(7),streak}):individualVal;
          const unit=ch.metric==="protein"?(ch.mode==="shared"?"protein days":"protein grams"):ch.metric==="volume"?(ch.mode==="shared"?"lbs":wUnit(s.units)):ch.metric;
          // Shared goal: sum every member's latest snapshot for this metric (my own value comes
          // from local data so it's live even before the next sync).
          const SNAP={streak:"streak",workouts:"weekWorkouts",volume:"weekVol",protein:"macro"};
          const others=(members?.members||[]).filter(m=>m.email!==email);
          const groupVal=ch.mode==="shared"?others.reduce((a2,m)=>a2+(parseInt(m.challenges?.find(c3=>c3.challenge_id===SNAP[ch.metric])?.value)||0),0)+myVal:myVal;
          const shown=ch.mode==="shared"?groupVal:myVal;
          const pct=Math.min(100,Math.round(shown/ch.target*100));
          const myShare=ch.mode==="shared"&&groupVal>0?Math.round(myVal/groupVal*100):null;
          return(
            <Card key={i} style={{padding:14}}>
              <div style={{display:"flex",justifyContent:"space-between",marginBottom:6}}>
                <div style={{fontSize:13,fontWeight:700,color:V.text,display:"flex",alignItems:"center",gap:6}}>{ch.name}
                  {ch.mode==="shared"&&<span style={{fontSize:11,fontWeight:700,padding:"2px 7px",borderRadius:6,background:`${V.purple}18`,color:V.purple}}>Shared</span>}</div>
                <div style={{display:"flex",gap:6,alignItems:"center"}}>
                  <span style={{fontSize:11,color:pct>=100?V.accent:V.text3,fontWeight:700}}>{pct}%</span>
                  {ch.createdBy===email&&<button onClick={()=>removeChal(ch.id)} style={{background:"none",border:"none",cursor:"pointer",padding:2,fontSize:11,color:V.text3}}>X</button>}
                </div>
              </div>
              <div style={{height:6,borderRadius:3,background:V.hoverBg,marginBottom:6}}>
                <div style={{height:"100%",borderRadius:3,background:pct>=100?V.accent:V.warn,width:`${pct}%`,transition:"width .3s"}}/>
              </div>
              <div style={{display:"flex",justifyContent:"space-between",fontSize:11,color:V.text3}}>
                {ch.mode==="shared"
                  ?<span>Group {unit} (last 7 days): <span style={{color:V.accent,fontWeight:700}}>{groupVal.toLocaleString()}</span> / {ch.target.toLocaleString()}{myShare!=null&&<span> · you {myVal.toLocaleString()} ({myShare}%)</span>}</span>
                  :<span>Your {unit}: <span style={{color:V.accent,fontWeight:700}}>{myVal.toLocaleString()}</span> / {ch.target.toLocaleString()}</span>}
                {ch.endDate&&<span>Ends {ch.endDate}</span>}
              </div>
            </Card>
          );
        }):(
          <Card style={{padding:20,textAlign:"center"}}><div style={{fontSize:12,color:V.text3}}>No group challenges yet. Create one!</div></Card>
        )}
      </>)}

      {/* FEED TAB */}
      {groupTab==="feed"&&(<>
        {/* #3: Weekly recap */}
        {(()=>{
          const mems=members?.members||[];
          const totalWorkouts=groupFeed.filter(e=>e.type==="WorkoutLogged"&&new Date(e.created_at)>new Date(Date.now()-7*86400000)).length;
          const totalPRs=groupFeed.filter(e=>e.type==="PRHit"&&new Date(e.created_at)>new Date(Date.now()-7*86400000)).length;
          const topMember=(()=>{
            const counts={};groupFeed.filter(e=>e.type==="WorkoutLogged"&&new Date(e.created_at)>new Date(Date.now()-7*86400000))
              .forEach(e=>{counts[e.name]=(counts[e.name]||0)+1;});
            const sorted=Object.entries(counts).sort((a,b)=>b[1]-a[1]);
            return sorted[0]?{name:sorted[0][0],count:sorted[0][1]}:null;
          })();
          const topStreak=mems.reduce((best,m)=>{
            const sv=parseInt(m.challenges?.find(c=>c.challenge_id==="streak")?.value||0);
            return sv>best.val?{name:m.name,val:sv}:best;
          },{name:"",val:0});
          if(totalWorkouts===0&&mems.length===0)return null;
          return(
            <Card style={{padding:14,border:`1px solid ${V.purple}20`,background:`${V.purple}04`,marginBottom:6}}>
              <SectionTitle icon={Icons.chart} color={V.purple} size={12} style={{marginBottom:8}}>This week</SectionTitle>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8,marginBottom:8}}>
                <div style={{textAlign:"center"}}>
                  <div style={{fontSize:18,fontWeight:800,color:V.accent,fontFamily:V.mono}}>{totalWorkouts}</div>
                  <div style={{fontSize:11,color:V.text3}}>WORKOUTS</div>
                </div>
                <div style={{textAlign:"center"}}>
                  <div style={{fontSize:18,fontWeight:800,color:V.warn,fontFamily:V.mono}}>{totalPRs}</div>
                  <div style={{fontSize:11,color:V.text3}}>PRs HIT</div>
                </div>
                <div style={{textAlign:"center"}}>
                  <div style={{fontSize:18,fontWeight:800,color:V.purple,fontFamily:V.mono}}>{mems.length}</div>
                  <div style={{fontSize:11,color:V.text3}}>MEMBERS</div>
                </div>
              </div>
              {topMember&&<div style={{fontSize:11,color:V.text2}}>👑 <span style={{fontWeight:700,color:V.accent}}>{topMember.name}</span> led with {topMember.count} workout{topMember.count>1?"s":""}</div>}
              {topStreak.val>0&&<div style={{fontSize:11,color:V.text2,marginTop:2}}>🔥 Top streak: <span style={{fontWeight:700,color:V.warn}}>{topStreak.name}</span> ({topStreak.val} days)</div>}
            </Card>
          );
        })()}
        <div style={{fontSize:11,color:V.text3,marginBottom:4}}>Recent activity from group members</div>
        {groupFeed.length>0?groupFeed.slice(0,20).map((ev,i)=>(
          <Card key={i} style={{padding:12}}>
            <div style={{display:"flex",alignItems:"center",gap:8}}>
              <div style={{width:28,height:28,borderRadius:8,background:`linear-gradient(135deg,${V.accent},${V.purple})`,
                display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                <span style={{fontSize:11,color:V.bg,fontWeight:900}}>{(ev.name||"?")[0].toUpperCase()}</span>
              </div>
              <div style={{flex:1}}>
                <div style={{fontSize:12,fontWeight:600,color:ev.isOwn?V.accent:V.text}}>{ev.name||"Member"}</div>
                <div style={{fontSize:11,color:V.text3}}>
                  {ev.type==="WorkoutLogged"?`Logged workout (${ev.data?.exercises?.length||0} exercises)`:
                   ev.type==="PRHit"?`PR: ${ev.data?.exercise} ${ev.data?.weight}×${ev.data?.reps} 🎉`:
                   ev.type==="GroupChat"?ev.data?.text:
                   ev.type==="GroupChallengeCreated"?`Created challenge: ${ev.data?.challenge}`:
                   ev.type==="QuickMessage"?`${ev.data?.msg}`:ev.type}
                </div>
              </div>
              <div style={{fontSize:11,color:V.text3}}>{(()=>{const m2=Math.round((Date.now()-new Date(ev.created_at))/60000);return m2<60?`${m2}m`:m2<1440?`${Math.round(m2/60)}h`:`${Math.round(m2/1440)}d`;})()}</div>
            </div>
            {/* Reactions */}
            <ReactionRow ev={ev} compact onReact={(id,key)=>{
              setGroupFeed(f=>f.map(e=>e.id===id?applyReactionToggle(e,key):e));
              LS.set("ft-reacted-today",today());
              SocialAPI.react(email,id,key);
            }}/>
          </Card>
        )):(
          <Card style={{padding:20,textAlign:"center"}}><div style={{fontSize:12,color:V.text3}}>No activity yet</div></Card>
        )}
      </>)}

      {/* CHAT TAB */}
      {groupTab==="chat"&&(<>
        <div style={{flex:1,display:"flex",flexDirection:"column",gap:4,maxHeight:400,overflowY:"auto",WebkitOverflowScrolling:"touch"}}>
          {chatMessages.length===0&&<div style={{textAlign:"center",padding:30,fontSize:12,color:V.text3}}>No messages yet</div>}
          {chatMessages.map((msg,i)=>{
            const isMe=msg.from===email;
            return(
              <div key={i} style={{display:"flex",flexDirection:"column",alignItems:isMe?"flex-end":"flex-start",marginBottom:4}}>
                {!isMe&&<div style={{fontSize:11,color:V.text3,marginBottom:2,marginLeft:4}}>{msg.name}</div>}
                <div style={{padding:"8px 12px",borderRadius:isMe?"14px 14px 4px 14px":"14px 14px 14px 4px",
                  background:isMe?V.accent:V.hoverBg,maxWidth:"80%"}}>
                  {(msg.imageData||msg.gifUrl)&&<img src={msg.imageData||msg.gifUrl} alt={msg.gifUrl?"Group GIF":"Group photo"}
                    loading="lazy" referrerPolicy="no-referrer" style={{display:"block",maxWidth:"100%",maxHeight:280,borderRadius:8,objectFit:"contain"}}/>}
                  {msg.text&&<div style={{fontSize:12,color:isMe?V.bg:V.text}}>{msg.text}</div>}
                </div>
                <div style={{fontSize:11,color:V.text3,marginTop:1}}>{new Date(msg.ts).toLocaleTimeString([],{hour:"numeric",minute:"2-digit"})}</div>
              </div>
            );
          })}
        </div>
        <div style={{display:"flex",gap:8,marginTop:8,alignItems:"flex-end"}}>
          <div style={{flex:1,position:"relative"}}>
            <input value={chatMsg} onChange={e=>{if(e.target.value.length<=200)setChatMsg(e.target.value);}} placeholder="Message..." maxLength={200}
              onKeyDown={e=>{if(e.key==="Enter")sendChat();}}
              style={{width:"100%",padding:"10px 14px",background:V.card,border:`1px solid ${V.cardBorder}`,borderRadius:20,color:V.text,fontSize:13,outline:"none",fontFamily:V.font,boxSizing:"border-box"}}/>
            {chatMsg.length>0&&<span style={{position:"absolute",right:12,bottom:-14,fontSize:11,
              color:chatMsg.length>180?V.danger:V.text3}}>{chatMsg.length}/200</span>}
          </div>
          <button onClick={sendChat} disabled={!chatMsg.trim()||chatMsg.length>200} style={{width:40,height:40,borderRadius:20,
            background:chatMsg.trim()?V.accent:V.hoverBg,border:"none",cursor:chatMsg.trim()?"pointer":"default",
            display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={chatMsg.trim()?V.bg:V.text3} strokeWidth="2" strokeLinecap="round">
              <line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/>
            </svg>
          </button>
        </div>
      </>)}

      {/* ── Members tab (with roles) ── */}
      {groupTab==="members"&&(
        <div style={{display:"flex",flexDirection:"column",gap:4}}>
          {(()=>{const myRole=members?.members?.find(m=>m.email===email)?.role;const isOwner=myRole==="owner";const isAdmin=myRole==="admin"||isOwner;
          return(<>
            {/* Pin announcement button */}
            {isAdmin&&(<Btn v="secondary" full onClick={()=>{
              const text=prompt("Enter announcement text (max 500 chars):");
              if(text?.trim()){SocialAPI.pinMessage(email,selGroup,text.trim());loadGroups();SuccessToastCtrl.show("Announcement pinned");}
            }}>📌 Pin Announcement</Btn>)}
            {(members?.members||[]).map((m,i)=>{
              const isMe=m.email===email;const roleIcon=m.role==="owner"?"👑":m.role==="admin"?"⭐":"";
              return(
                <Card key={m.email||i} style={{padding:10}}>
                  <div style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                    <div style={{display:"flex",alignItems:"center",gap:8}}>
                      <div style={{width:28,height:28,borderRadius:8,background:`linear-gradient(135deg,${V.purple},#ec4899)`,
                        display:"flex",alignItems:"center",justifyContent:"center"}}>
                        <span style={{fontSize:11,color:"#fff",fontWeight:800}}>{(m.name||"?")[0].toUpperCase()}</span>
                      </div>
                      <div>
                        <div style={{fontSize:12,fontWeight:600,color:isMe?V.accent:V.text}}>{m.name||"Unknown"}{isMe?" (You)":""} {roleIcon}</div>
                        {m.username&&<div style={{fontSize:11,color:V.text3}}>@{m.username}</div>}
                      </div>
                    </div>
                    {isOwner&&!isMe&&(
                      <div style={{display:"flex",gap:4}}>
                        <button onClick={async()=>{
                          const newRole=m.role==="admin"?"member":"admin";
                          const r=await SocialAPI.setGroupRole(email,selGroup,m.email,newRole);
                          if(r?.success){SuccessToastCtrl.show(newRole==="admin"?"Promoted to admin":"Demoted to member");refreshMembers(selGroup);}
                        }} style={{padding:"3px 8px",borderRadius:5,background:`${V.accent}10`,border:`1px solid ${V.accent}25`,
                          cursor:"pointer",fontSize:11,color:V.accent,fontFamily:V.font}}>
                          {m.role==="admin"?"Demote":"Promote"}
                        </button>
                        <button onClick={()=>ConfirmCtrl.show(`Kick ${m.name}?`,"They can rejoin with the code.",async()=>{
                          await SocialAPI.kickMember(email,selGroup,m.email);refreshMembers(selGroup);SuccessToastCtrl.show("Member removed");
                        })} style={{padding:"3px 8px",borderRadius:5,background:`${V.danger}10`,border:`1px solid ${V.danger}25`,
                          cursor:"pointer",fontSize:11,color:V.danger,fontFamily:V.font}}>Kick</button>
                      </div>
                    )}
                    {isAdmin&&!isOwner&&!isMe&&m.role!=="owner"&&(
                      <button onClick={()=>ConfirmCtrl.show(`Kick ${m.name}?`,"They can rejoin with the code.",async()=>{
                        await SocialAPI.kickMember(email,selGroup,m.email);refreshMembers(selGroup);SuccessToastCtrl.show("Member removed");
                      })} style={{padding:"3px 8px",borderRadius:5,background:`${V.danger}10`,border:`1px solid ${V.danger}25`,
                        cursor:"pointer",fontSize:11,color:V.danger,fontFamily:V.font}}>Kick</button>
                    )}
                  </div>
                </Card>
              );
            })}
          </>);})()}
        </div>
      )}
    </div>
  );

  // ─── Groups List ───
  return(
    <div style={{display:"flex",flexDirection:"column",gap:10}}>
      <div style={{fontSize:16,fontWeight:800,color:V.text}}>Groups</div>
      {loadErr&&<SocialErrorCard onRetry={loadGroups}/>}
      <Card style={{padding:14}}>
        <div style={{fontSize:12,fontWeight:700,color:V.text,marginBottom:6}}>Join a Group</div>
        <div style={{display:"flex",gap:8}}>
          <input value={code} onChange={e=>setCode(e.target.value.toUpperCase())} placeholder="Enter code" maxLength={8}
            style={{flex:1,padding:"8px 12px",background:V.inputBg,border:`1px solid ${V.cardBorder}`,borderRadius:8,color:V.text,fontSize:13,outline:"none",fontFamily:V.mono,letterSpacing:3}}/>
          <Btn onClick={join} disabled={code.length<4}>Join</Btn>
        </div>
      </Card>
      {!showCreate?<Btn v="secondary" full onClick={()=>setShowCreate(true)}>Create a Group</Btn>:(
        <Card style={{padding:14}}>
          <input value={newName} onChange={e=>setNewName(e.target.value)} placeholder="Group name"
            style={{width:"100%",padding:"8px 12px",background:V.inputBg,border:`1px solid ${V.cardBorder}`,borderRadius:8,color:V.text,fontSize:13,outline:"none",fontFamily:V.font,boxSizing:"border-box",marginBottom:8}}/>
          <div style={{display:"flex",gap:8}}><Btn v="secondary" onClick={()=>setShowCreate(false)}>Cancel</Btn><Btn full onClick={create}>Create</Btn></div>
        </Card>
      )}

      {/* ── Discover Groups (global) ── */}
      <Card style={{padding:14}}>
        <button onClick={()=>setShowDiscoverG(!showDiscoverG)} style={{width:"100%",display:"flex",justifyContent:"space-between",alignItems:"center",background:"none",border:"none",cursor:"pointer",padding:0}}>
          <span style={{fontSize:12,fontWeight:700,color:V.accent}}>🌍 Browse All Groups</span>
          <span style={{fontSize:11,color:V.text3}}>{showDiscoverG?"▲":"▼"}</span>
        </button>
        {showDiscoverG&&<div style={{marginTop:10}}>
          <div style={{display:"flex",gap:8,marginBottom:10}}>
            <input value={discoverGQ} onChange={e=>setDiscoverGQ(e.target.value)}
              onKeyDown={e=>{if(e.key==="Enter"){setDiscoverGPage(1);discoverGSearch(discoverGQ,1);}}}
              placeholder="Search groups..."
              style={{flex:1,padding:"8px 12px",background:V.inputBg,border:`1px solid ${V.cardBorder}`,borderRadius:8,color:V.text,fontSize:12,outline:"none",fontFamily:V.font}}/>
            <Btn v="secondary" onClick={()=>{setDiscoverGPage(1);discoverGSearch(discoverGQ,1);}}>Search</Btn>
          </div>
          {discoverGLoading&&<div style={{textAlign:"center",padding:12,fontSize:11,color:V.text3}}>Loading...</div>}
          {!discoverGLoading&&discoverGResults&&(discoverGResults.groups||[]).length===0&&<div style={{textAlign:"center",padding:12,fontSize:11,color:V.text3}}>No groups found</div>}
          {!discoverGLoading&&(discoverGResults?.groups||[]).map((g,i)=>(
            <div key={g.code} style={{display:"flex",alignItems:"center",gap:10,padding:"10px 0",borderBottom:i<discoverGResults.groups.length-1?`1px solid ${V.divider}`:"none"}}>
              <div style={{width:32,height:32,borderRadius:8,background:`linear-gradient(135deg,${V.accent},${V.purple})`,
                display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                {Icons.users({size:16,color:"#fff"})}
              </div>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontSize:12,fontWeight:600,color:V.text}}>{g.name}</div>
                <div style={{fontSize:11,color:V.text3}}>{g.memberCount} member{g.memberCount!==1?"s":""}{g.description?` · ${g.description.slice(0,40)}${g.description.length>40?"...":""}`:""}</div>
              </div>
              {g.joined?<span style={{fontSize:11,color:V.accent,fontWeight:600}}>Joined</span>
              :<button onClick={async()=>{
                const r=await SocialAPI.joinGroup(email,g.code);
                if(r?.success){SuccessToastCtrl.show(`Joined ${g.name}`);discoverGSearch(discoverGQ,discoverGPage);loadGroups();}
                else SuccessToastCtrl.error(r?.error||"Failed to join");
              }} style={{padding:"4px 10px",borderRadius:6,background:V.accent,border:"none",cursor:"pointer",fontSize:11,fontWeight:700,color:V.bg,fontFamily:V.font}}>Join</button>}
            </div>
          ))}
          {!discoverGLoading&&discoverGResults&&discoverGResults.total>discoverGResults.groups?.length&&(
            <div style={{display:"flex",justifyContent:"center",gap:8,marginTop:8}}>
              {discoverGPage>1&&<Btn v="secondary" onClick={()=>{const p=discoverGPage-1;setDiscoverGPage(p);discoverGSearch(discoverGQ,p);}}>← Prev</Btn>}
              <Btn v="secondary" onClick={()=>{const p=discoverGPage+1;setDiscoverGPage(p);discoverGSearch(discoverGQ,p);}}>Next →</Btn>
            </div>
          )}
        </div>}
      </Card>

      {groups?.groups?.map(g=>(
        <Card key={g.code} style={{padding:14,cursor:"pointer"}} onClick={()=>openGroup(g.code)}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <div>
              <div style={{fontSize:14,fontWeight:700,color:V.text}}>{g.name}</div>
              <div style={{fontSize:11,color:V.text3}}>{g.code} · {g.member_count} member{g.member_count!==1?"s":""}</div>
            </div>
            <div style={{display:"flex",gap:6,alignItems:"center"}}>
              <button onClick={e=>{e.stopPropagation();leave(g.code);}} style={{padding:"4px 10px",borderRadius:6,background:V.inputBg,border:`1px solid ${V.cardBorder}`,cursor:"pointer",fontSize:11,color:V.text3,fontFamily:V.font}}>Leave</button>
              {Icons.chevRight({size:14,color:V.text3})}
            </div>
          </div>
        </Card>
      ))}
      {(!groups?.groups||groups.groups.length===0)&&!loadErr&&<div style={{textAlign:"center",padding:20,fontSize:12,color:V.text3}}>No groups yet. Join or create one!</div>}
    </div>
  );
}


// ─── Social Sub-Page: Profile ───
